import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import useAuthAxios from "../utils/baseAxios";
import { API } from "../constants/api";
import { errorMessage } from "../utils/errorMessage";
import { useUser } from "../context/user-context";
import {
  buildNairaWalletPayload,
  NairaWalletFieldDefinition,
} from "../utils/nairaWalletFields";

type UseCreateNairaWalletOptions = {
  onSuccess?: () => void;
};

const useCreateNairaWallet = (options?: UseCreateNairaWalletOptions) => {
  const axiosInstance = useAuthAxios();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { refetch: refetchUser } = useUser();

  return useMutation({
    mutationFn: async (payload: Record<string, string>) => {
      const response = await axiosInstance.post(API.createNairaWallet, payload);
      return response.data;
    },
    onSuccess: async (response) => {
      toast.success(response?.message ?? "Naira wallet created successfully");
      await queryClient.invalidateQueries({ queryKey: ["naira-wallet-status"] });
      await queryClient.invalidateQueries({ queryKey: ["naira-wallet"] });
      await refetchUser();
      options?.onSuccess?.();
      navigate("/wallet", { replace: true });
    },
    onError: (error) => {
      const err = error as { message?: string; data?: { message?: string } };
      toast.error(errorMessage(err?.message || err?.data?.message));
    },
  });
};

export const submitNairaWalletFromFields = (
  mutation: ReturnType<typeof useCreateNairaWallet>,
  fieldDefinitions: NairaWalletFieldDefinition[],
  formValues: Record<string, string>
) => {
  mutation.mutate(buildNairaWalletPayload(fieldDefinitions, formValues));
};

export const submitEmptyNairaWallet = (
  mutation: ReturnType<typeof useCreateNairaWallet>
) => {
  mutation.mutate({});
};

export default useCreateNairaWallet;
